import { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import WelcomeSection from './components/WelcomeSection';
import OurServicesCards from './components/OurServicesCards';
import About from './components/About';
import KitCTA from './components/KitCTA';
import ZonnicCTA from './components/ZonnicCTA';
import Care365CTA from './components/Care365CTA';
import Hours from './components/Hours';
import Footer from './components/Footer';
import MobileStickyCTA from '@/components/feature/MobileStickyCTA';
import { useSEO } from '../../utils/seo';
import AreasOfFocus from './components/AreasOfFocus';
import { usePharmacy } from '@/context/PharmacyContext';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { settings } = usePharmacy();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';
  const pharmacyName = settings?.pharmacy_name || 'Thornhill Drug-Mart';
  const phone = settings?.phone || '(905) 709-9985';
  const businessHours = settings?.business_hours || [];

  const openingHoursSpec = businessHours.map((bh: any) => ({
    '@type': 'OpeningHoursSpecification' as const,
    dayOfWeek: bh.day,
    opens: bh.open || '08:00',
    closes: bh.close || '17:00',
  }));

  useSEO({
    title: `${pharmacyName} | Methadone & Addiction Treatment Pharmacy`,
    description: `${pharmacyName} in ${settings?.city || 'Thornhill'}, Ontario. We provide life-saving overdose prevention resources, pharmacy services, and addiction support through the Ontario Naloxone Program.`,
    keywords: `free naloxone kits ${settings?.city || 'Thornhill'}, pharmacy services Ontario, overdose prevention, addiction services, ${pharmacyName}`,
    canonical: siteUrl,
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Pharmacy",
      "name": pharmacyName,
      "description": `Professional pharmacy providing life-saving overdose prevention resources, pharmacy services, and addiction support through the Ontario Naloxone Program in ${settings?.city || 'Thornhill'}, Ontario`,
      "url": siteUrl,
      "logo": "https://static.readdy.ai/image/621fe86c950ded9745dd2c19cd7c5595/c0b5a15a2b815315bfd2ccbd2c453721.png",
      "image": "https://static.readdy.ai/image/621fe86c950ded9745dd2c19cd7c5595/c0b5a15a2b815315bfd2ccbd2c453721.png",
      "telephone": `+1-${settings?.phone_raw || '9057099985'}`,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": settings?.address_line1 || "8108 Yonge St Unit 3-4",
        "addressLocality": settings?.city || "Thornhill",
        "addressRegion": settings?.province || "ON",
        "postalCode": settings?.postal_code || "L4J 1W4",
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": settings?.latitude || "43.8184",
        "longitude": settings?.longitude || "-79.4278"
      },
      "openingHoursSpecification": openingHoursSpec,
      "areaServed": [
        { "@type": "City", "name": "Thornhill" },
        { "@type": "City", "name": "North York" },
        { "@type": "City", "name": "Richmond Hill" },
        { "@type": "City", "name": "Vaughan" },
        { "@type": "City", "name": "Toronto" }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Pharmacy and Addiction Treatment Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Free Naloxone Kits",
              "description": "Life-saving naloxone kits available through the Ontario Naloxone Program for overdose prevention"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Methadone Treatment",
              "description": "Professional methadone maintenance treatment for opioid addiction recovery"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Suboxone Treatment",
              "description": "Comprehensive suboxone therapy for addiction treatment and recovery support"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Smoking Cessation",
              "description": "Expert guidance and products to help quit smoking including Zonnic pouches"
            }
          }
        ]
      }
    }
  });

  return (
    <div className="min-h-screen">
      <Navbar isScrolled={isScrolled} />
      <Hero />
      <WelcomeSection />
      <OurServicesCards />
      <About />
      <KitCTA />
      <ZonnicCTA />
      <Care365CTA />
      <AreasOfFocus />
      <Hours />
      <MobileStickyCTA />
      <Footer />
    </div>
  );
}